import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ImageIcon from "@mui/icons-material/Image";
import Tooltip from "@mui/material/Tooltip";
import CustomMarkdownEditor from "./CustomMarkdownEditor";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import PlaceholderInput from "./PlaceholderInput";

// Utility function for file size validation
const validateFileSize = (file) => {
  if (!file) return { isValid: true };

  const maxSizes = {
    "video/mp4": 16 * 1024 * 1024, // 16MB
    "image/jpeg": 5 * 1024 * 1024, // 5MB
    "image/jpg": 5 * 1024 * 1024, // 5MB
    "image/png": 5 * 1024 * 1024, // 5MB
  };

  const maxSize = maxSizes[file.type];
  if (maxSize && file.size > maxSize) {
    const sizeLimit = file.type === "video/mp4" ? "16MB" : "5MB";
    const fileType = file.type === "video/mp4" ? "video" : "image";

    return {
      isValid: false,
      error: `File size exceeds the limit of ${sizeLimit} for ${fileType} files.`,
    };
  }

  return { isValid: true };
};

const CarouselTemplateEditor = ({
  cards,
  setCards,
  mainBody,
  setMainBody,
  mainBodySampleFields,
  setMainBodySampleFields,
  carouselType,
  setCarouselType,
  carouselMainBodyDuplicatePlaceholderError,
  setCarouselMainBodyDuplicatePlaceholderError,
  carouselCardBodyDuplicatePlaceholderError,
  setCarouselCardBodyDuplicatePlaceholderError,
}) => {
  const [currentCardIndex, setCurrentCardIndex] = useState(0);

  const [fileErrors, setFileErrors] = useState({});

  // Function to detect placeholders in text
  const detectPlaceholders = (text) => {
    const matches = text.match(/\{\{\d+\}\}/g) || [];
    const seen = new Set();
    let hasDuplicate = false;
    matches.forEach((match) => {
      if (seen.has(match)) hasDuplicate = true;
      else seen.add(match);
    });
    const uniquePlaceholders = matches.filter(
      (match, idx) => matches.indexOf(match) === idx
    );
    return { hasDuplicate, uniquePlaceholders };
  };

  // Function to handle main body text changes
  const handleMainBodyChange = (newBody) => {
    setMainBody(newBody);
    const { hasDuplicate, uniquePlaceholders } = detectPlaceholders(newBody);
    setCarouselMainBodyDuplicatePlaceholderError(hasDuplicate);
    if (!hasDuplicate) {
      const newSamples = uniquePlaceholders.map((_, idx) =>
        mainBodySampleFields[idx] !== undefined ? mainBodySampleFields[idx] : ""
      );
      setMainBodySampleFields(newSamples);
    }
    // On a transient duplicate, keep existing sample fields as-is
    // so previously entered values aren't lost once it resolves.
  };

  // Function to update sample fields for main body
  const updateMainBodySampleField = (index, value) => {
    const newSampleFields = [...mainBodySampleFields];
    newSampleFields[index] = value;
    setMainBodySampleFields(newSampleFields);
  };

  // Add a new card with unique properties
  const addCard = () => {
    if (cards.length < 10) {
      const newCard = {
        id: Date.now(),
        headerType: carouselType.toUpperCase(), // Use the correct type based on carouselType
        headerFile: null,
        body: "",
        sampleBodyFields: [],
        buttons: [
          {
            type: "QUICK_REPLY",
            data: {
              buttonText: "",
            },
          },
        ],
      };

      // Use functional update to ensure we're working with the latest state
      setCards((prevCards) => [...prevCards, newCard]);

      // Switch to the new card (use the new length after adding)
      setCurrentCardIndex(cards.length);
    }
  };

  // Remove a card
  const removeCard = (index) => {
    if (cards.length > 1) {
      // Use functional update to ensure we're working with the latest state
      setCards((prevCards) => {
        const newCards = [...prevCards];
        newCards.splice(index, 1);
        return newCards;
      });

      // Update current index if needed
      if (currentCardIndex >= index && currentCardIndex > 0) {
        setCurrentCardIndex(currentCardIndex - 1);
      }
    }
  };

  // Handle card body text change - completely independent update
  const handleBodyChange = (index, newBody) => {
    setCards((prevCards) => {
      const newCards = prevCards.map((card, i) => {
        if (i === index) {
          const { hasDuplicate, uniquePlaceholders } =
            detectPlaceholders(newBody);
          // Update error state for this card
          setCarouselCardBodyDuplicatePlaceholderError((prev) => {
            const updated = [...prev];
            updated[index] = hasDuplicate;
            return updated;
          });
          // Only update sample fields if no error
          // On a transient duplicate, keep existing sample fields as-is
          // so previously entered values aren't lost once it resolves.
          const newSampleFields = !hasDuplicate
            ? uniquePlaceholders.map((_, idx) =>
                card.sampleBodyFields &&
                card.sampleBodyFields[idx] !== undefined
                  ? card.sampleBodyFields[idx]
                  : ""
              )
            : card.sampleBodyFields;
          return {
            ...card,
            body: newBody,
            sampleBodyFields: newSampleFields,
          };
        }
        return card;
      });
      return newCards;
    });
  };

  // Add this function to update the sample body field values
  const updateSampleBodyField = (cardIndex, placeholderIndex, value) => {
    setCards((prevCards) => {
      const newCards = [...prevCards];
      // Initialize sampleBodyFields array if it doesn't exist or is empty
      if (
        !newCards[cardIndex].sampleBodyFields ||
        newCards[cardIndex].sampleBodyFields.length === 0
      ) {
        const placeholdersCount = (
          newCards[cardIndex].body.match(/\{\{\d+\}\}/g) || []
        ).length;
        newCards[cardIndex].sampleBodyFields =
          Array(placeholdersCount).fill("");
      }

      // Now safely update the sample field
      const newSampleFields = [...newCards[cardIndex].sampleBodyFields];
      const valuesList = ["name", "age", "gender", "city", "email", "custom"];

      newSampleFields[placeholderIndex] = {
        type: valuesList.includes(value) ? value : "custom",
        value: value,
      };
      newCards[cardIndex] = {
        ...newCards[cardIndex],
        sampleBodyFields: newSampleFields,
      };

      return newCards;
    });
  };

  // Handle card image file change
  const handleHeaderFileChange = (index, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // File type validation
    const allowedTypes =
      carouselType === "video"
        ? ["video/mp4"]
        : ["image/jpeg", "image/jpg", "image/png"];

    if (!allowedTypes.includes(file.type)) {
      setFileErrors((prev) => ({
        ...prev,
        [index]: `Invalid file type. Please upload a ${
          carouselType === "video" ? "video (.mp4)" : "image (.jpg, .png)"
        } file.`,
      }));
      event.target.value = "";
      return;
    }

    // Validate file size
    const validation = validateFileSize(file);

    if (!validation.isValid) {
      setFileErrors((prev) => ({
        ...prev,
        [index]: validation.error,
      }));
      event.target.value = "";
      return;
    }

    // Clear any previous error for this card
    setFileErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });

    // Set the file if validation passes
    setCards((prevCards) => {
      const newCards = [...prevCards];
      newCards[index] = {
        ...newCards[index],
        headerFile: file,
      };
      return newCards;
    });

    console.log(`File selected for card ${index}:`, file.name);
  };

  // Delete header file
  const handleDeleteHeaderFile = (index) => {
    // Clear file error for this card when file is deleted
    setFileErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });

    // Use functional update to remove the file
    setCards((prevCards) => {
      const newCards = [...prevCards];
      newCards[index] = {
        ...newCards[index],
        headerFile: null,
      };
      return newCards;
    });
  };

  // Add a button to a card
  const addButton = (cardIndex) => {
    // Use functional update
    setCards((prevCards) => {
      const newCards = [...prevCards];
      if (newCards[cardIndex].buttons.length < 2) {
        // WhatsApp typically limits to 2 buttons per card
        newCards[cardIndex] = {
          ...newCards[cardIndex],
          buttons: [
            ...newCards[cardIndex].buttons,
            {
              type: "QUICK_REPLY",
              data: {
                buttonText:
                  "Button " + (newCards[cardIndex].buttons.length + 1),
              },
            },
          ],
        };
      }
      return newCards;
    });
  };

  // Remove a button from a card
  const removeButton = (cardIndex, buttonIndex) => {
    // Use functional update
    setCards((prevCards) => {
      const newCards = [...prevCards];
      const newButtons = [...newCards[cardIndex].buttons];
      newButtons.splice(buttonIndex, 1);
      newCards[cardIndex] = {
        ...newCards[cardIndex],
        buttons: newButtons,
      };
      return newCards;
    });
  };

  // Update button data
  const updateButtonData = (cardIndex, buttonIndex, field, value) => {
    // Use functional update
    setCards((prevCards) => {
      const newCards = [...prevCards];
      const newButton = {
        ...newCards[cardIndex].buttons[buttonIndex],
        data: {
          ...newCards[cardIndex].buttons[buttonIndex].data,
          [field]: value,
        },
      };
      const newButtons = [...newCards[cardIndex].buttons];
      newButtons[buttonIndex] = newButton;
      newCards[cardIndex] = {
        ...newCards[cardIndex],
        buttons: newButtons,
      };
      return newCards;
    });
  };

  // Update button type
  const updateButtonType = (cardIndex, buttonIndex, newType) => {
    // Use functional update
    setCards((prevCards) => {
      const newCards = [...prevCards];
      const button = newCards[cardIndex].buttons[buttonIndex];

      // Initialize appropriate data structure based on button type
      let newData = {};
      if (newType === "visit-website" || newType === "URL") {
        newData = {
          buttonText: button.data?.buttonText || "Visit Website",
          url: button.data?.url || "https://",
          sampleUrl: button.data?.sampleUrl || "example.com",
        };
      } else if (
        newType === "call-phone-number" ||
        newType === "PHONE_NUMBER"
      ) {
        newData = {
          buttonText: button.data?.buttonText || "Call Us",
          countryCode: button.data?.countryCode || "+1",
          phoneNumber: button.data?.phoneNumber || "",
        };
      } else {
        // QUICK_REPLY or marketing-opt-out
        newData = {
          buttonText: button.data?.buttonText || "",
        };
      }

      const newButton = {
        ...button,
        type: newType,
        data: newData,
      };

      const newButtons = [...newCards[cardIndex].buttons];
      newButtons[buttonIndex] = newButton;
      newCards[cardIndex] = {
        ...newCards[cardIndex],
        buttons: newButtons,
      };

      return newCards;
    });
  };

  // For debugging
  useEffect(() => {
    console.log("Current cards state:", cards);
  }, [cards]);

  useEffect(() => {
    setFileErrors((prev) => {
      const newErrors = { ...prev };
      // Remove errors for card indices that no longer exist
      Object.keys(newErrors).forEach((key) => {
        const cardIndex = parseInt(key);
        if (cardIndex >= cards.length) {
          delete newErrors[cardIndex];
        }
      });
      return newErrors;
    });
  }, [cards]);

  // Check if the current card exists before rendering its editor
  const currentCardExists = cards.length > 0 && currentCardIndex < cards.length;
  const currentCard = currentCardExists ? cards[currentCardIndex] : null;

  console.log("currentCard length: ", currentCard.buttons.length);

  return (
    <Stack spacing={4}>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ marginBottom: 1 }}>
            Carousel Type
          </Typography>
          <FormControl sx={{ flex: 1 }} component="fieldset">
            <RadioGroup
              aria-labelledby="carousel-type-label"
              name="carousel-type"
              value={carouselType}
              onChange={(event) => setCarouselType(event.target.value)}
              row
            >
              <FormControlLabel
                value="image"
                control={<Radio />}
                label="Image Carousel"
              />
              <FormControlLabel
                value="video"
                control={<Radio />}
                label="Video Carousel"
              />
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      {/* Main Body Message */}
      <Card>
        <CardContent>
          <Stack>
            <Typography variant="h6">Main Message body *</Typography>
            <Typography variant="body2" sx={{ marginBottom: 2 }}>
              This text will appear before the carousel cards.
            </Typography>
            <CustomMarkdownEditor
              value={mainBody}
              onChange={handleMainBodyChange}
              maxLength={1024}
            />
            {carouselMainBodyDuplicatePlaceholderError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                Duplicate variable placeholders are not allowed.
              </Typography>
            )}

            {/* Sample Fields for Main Body Placeholders */}
            {!carouselMainBodyDuplicatePlaceholderError &&
              mainBodySampleFields.length > 0 && (
                <Box sx={{ mb: 3, mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Body Variables
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      width: "100%",
                      gap: 4,
                    }}
                  >
                    {mainBodySampleFields.map((sample, index) => (
                      <Box
                        key={index}
                        sx={{
                          marginBottom: "16px",
                          display: "flex",
                          flex: { xs: 1, md: 0.5 },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ display: "inline-block", mr: 1 }}
                        >
                          {"{{" + (index + 1) + "}}:"}
                        </Typography>
                        <PlaceholderInput
                          sample={sample}
                          sampleBodyFields={mainBodySampleFields}
                          index={index}
                          setsampleBodyFields={setMainBodySampleFields}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
          </Stack>
        </CardContent>
      </Card>

      {/* Card Selection and Navigation */}
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 2,
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" sx={{ mr: 2 }}>
            Cards ({cards.length})
          </Typography>
          <Button
            startIcon={<AddCircleIcon />}
            variant="contained"
            color="primary"
            onClick={addCard}
            disabled={cards.length >= 10}
            sx={{ mr: 2 }}
          >
            Add Card
          </Button>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {cards.map((card, index) => (
            <Button
              key={card.id || index}
              variant={currentCardIndex === index ? "contained" : "outlined"}
              onClick={() => setCurrentCardIndex(index)}
              sx={{ minWidth: "40px" }}
            >
              {index + 1}
            </Button>
          ))}
        </Box>
      </Grid>

      {/* Current Card Editor */}
      {currentCard && (
        <Grid item xs={12} sx={{ mr: 2 }}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">
                    Card {currentCardIndex + 1}
                  </Typography>
                  <Button
                    startIcon={<DeleteIcon />}
                    color="error"
                    onClick={() => removeCard(currentCardIndex)}
                    disabled={cards.length <= 1}
                  >
                    Remove Card
                  </Button>
                </Box>

                {/* Media Upload Section */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Card Media ({carouselType === "video" ? "Video" : "Image"})
                    *
                  </Typography>

                  <Tooltip
                    title={
                      carouselType === "video"
                        ? "File size limit: 16MB"
                        : "File size limit: 5MB"
                    }
                    arrow
                    placement="top"
                  >
                    <span>
                      <input
                        accept={
                          carouselType === "video" ? "video/*" : "image/*"
                        }
                        id={`card-media-upload-${currentCardIndex}`}
                        type="file"
                        style={{ display: "none" }}
                        onChange={(e) =>
                          handleHeaderFileChange(currentCardIndex, e)
                        }
                      />
                      <label htmlFor={`card-media-upload-${currentCardIndex}`}>
                        <Button
                          variant="contained"
                          component="span"
                          startIcon={
                            carouselType === "video" ? (
                              <VideoLibraryIcon />
                            ) : (
                              <ImageIcon />
                            )
                          }
                        >
                          Choose{" "}
                          {carouselType === "video"
                            ? ".mp4 *"
                            : ".jpg or .png *"}
                        </Button>
                      </label>
                    </span>
                  </Tooltip>

                  {/* Display file validation error for current card */}
                  {fileErrors[currentCardIndex] && (
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{ mt: 1, display: "block" }}
                    >
                      {fileErrors[currentCardIndex]}
                    </Typography>
                  )}

                  {currentCard.headerFile && (
                    <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        {currentCard.headerFile.name}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteHeaderFile(currentCardIndex)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                {/* Card Body Text */}
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Card Body *
                  </Typography>

                  <CustomMarkdownEditor
                    key={`card-editor-${currentCardIndex}`} // Add key to force re-render
                    value={currentCard.body || ""}
                    onChange={(newValue) =>
                      handleBodyChange(currentCardIndex, newValue)
                    }
                    maxLength={60} // Carousel card bodies are typically limited to 60 characters
                  />
                </Box>
                
                {carouselCardBodyDuplicatePlaceholderError[
                  currentCardIndex
                ] && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    Duplicate variable placeholders are not allowed.
                  </Typography>
                )}

                {/* Sample Fields for Placeholders */}
                {/* {!carouselCardBodyDuplicatePlaceholderError[currentCardIndex] &&
                  currentCard.sampleBodyFields &&
                  currentCard.sampleBodyFields.length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" sx={{ mb: 2 }}>
                        Card Body Variables
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          width: "100%",
                          gap: 4,
                        }}
                      >
                        {currentCard.sampleBodyFields.map((sample, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              marginBottom: "16px",
                              flex: { xs: 1, md: 0.5 },
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ display: "inline-block", mr: 2 }}
                            >
                              {"{{" + (index + 1) + "}}:"}
                            </Typography>
                            <PlaceholderInput
                              cardBody={true}
                              sample={sample}
                              index={index}
                              sampleBodyFields={currentCard.sampleBodyFields}
                              setsampleBodyFields={(val) =>
                                updateSampleBodyField(
                                  currentCardIndex,
                                  index,
                                  val
                                )
                              }
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )} */}

                <Divider />

                {/* Buttons Section */}
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle1">
                      Buttons ({currentCard.buttons.length})
                    </Typography>
                    <Button
                      startIcon={<AddCircleIcon />}
                      variant="outlined"
                      onClick={() => addButton(currentCardIndex)}
                      disabled={currentCard.buttons.length >= 2}
                    >
                      Add Button
                    </Button>
                  </Box>

                  {currentCard.buttons.map((button, buttonIndex) => (
                    <Card
                      key={buttonIndex}
                      sx={{ mb: 2, p: 1, bgcolor: "#f8f8f8ff" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Typography variant="subtitle2">
                          Button {buttonIndex + 1}
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            removeButton(currentCardIndex, buttonIndex)
                          }
                          disabled={currentCard.buttons.length <= 1}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>

                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Button Type</InputLabel>
                        <Select
                          value={button.type}
                          label="Button Type"
                          onChange={(e) =>
                            updateButtonType(
                              currentCardIndex,
                              buttonIndex,
                              e.target.value
                            )
                          }
                        >
                          <MenuItem value="QUICK_REPLY">Quick Reply</MenuItem>
                          <MenuItem value="URL">URL (Visit Website)</MenuItem>
                          <MenuItem value="PHONE_NUMBER">Phone Number</MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        fullWidth
                        label="Button Text"
                        value={button.data?.buttonText || ""}
                        onChange={(e) =>
                          updateButtonData(
                            currentCardIndex,
                            buttonIndex,
                            "buttonText",
                            e.target.value
                          )
                        }
                        sx={{ mb: 2 }}
                        inputProps={{ maxLength: 20 }}
                        required
                      />

                      {button.type === "URL" && (
                        <>
                          <TextField
                            fullWidth
                            label="URL"
                            value={button.data?.url || ""}
                            onChange={(e) =>
                              updateButtonData(
                                currentCardIndex,
                                buttonIndex,
                                "url",
                                e.target.value
                              )
                            }
                            sx={{ mb: 2 }}
                            placeholder="https://example.com/{{1}}"
                          />
                          {button.data?.url &&
                            button.data.url.includes("{{1}}") && (
                              <TextField
                                fullWidth
                                label="Sample URL (replace {{1}})"
                                value={button.data?.sampleUrl || ""}
                                onChange={(e) =>
                                  updateButtonData(
                                    currentCardIndex,
                                    buttonIndex,
                                    "sampleUrl",
                                    e.target.value
                                  )
                                }
                              />
                            )}
                        </>
                      )}

                      {button.type === "PHONE_NUMBER" && (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <TextField
                            label="Country Code"
                            value={button.data?.countryCode || "+1"}
                            onChange={(e) =>
                              updateButtonData(
                                currentCardIndex,
                                buttonIndex,
                                "countryCode",
                                e.target.value
                              )
                            }
                            sx={{ width: "30%" }}
                          />
                          <TextField
                            fullWidth
                            label="Phone Number"
                            value={button.data?.phoneNumber || ""}
                            onChange={(e) =>
                              updateButtonData(
                                currentCardIndex,
                                buttonIndex,
                                "phoneNumber",
                                e.target.value
                              )
                            }
                          />
                        </Box>
                      )}
                    </Card>
                  ))}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Stack>
  );
};

export default CarouselTemplateEditor;
